import clsx from 'clsx'
import first from 'lodash/first'
import forEach from 'lodash/forEach'
import isBoolean from 'lodash/isBoolean'
import isNil from 'lodash/isNil'
import React, { Dispatch, FC, MutableRefObject, PropsWithChildren, ReactNode, SetStateAction, useImperativeHandle, useRef, useState } from 'react'

import { useTranslation } from '@Globals/Translations'

import styles from './Checkbox.module.scss'

export type CheckboxValidationResult = { error: string, success: boolean }
export type CheckboxValidationFn = (value: boolean) => CheckboxValidationResult

export type CheckboxActionProps = {
  validate: (value?: boolean) => boolean
}

export const useCheckboxActionRef = () => {
  const ref = useRef<CheckboxActionProps | null>(null)
  return ref
}

interface CheckboxProps {
  actionRef?: MutableRefObject<CheckboxActionProps | null>
  className?: string
  disabled?: boolean
  errorIcon?: ReactNode
  id?: string
  label?: string | ReactNode
  setInputValue: Dispatch<SetStateAction<boolean>> | ((value: boolean) => void)
  validations?: Array<CheckboxValidationFn>
  value: boolean
}

export const Checkbox: FC<PropsWithChildren<CheckboxProps>> = ({
  actionRef,
  errorIcon,
  className,
  disabled,
  id,
  label,
  validations = [],
  children,
  value,
  setInputValue,
}) => {
  const { t } = useTranslation()
  const [errors, setErrors] = useState<Array<CheckboxValidationResult>>([])

  const runValidations = (newValue?: boolean) => {
    if (disabled) {
      return errors.length === 0
    }
    if (!isNil(newValue)) {
      setInputValue(newValue)
    }
    const validationErrors: Array<CheckboxValidationResult> = []
    forEach(validations, (validationFn) => {
      const result: CheckboxValidationResult = validationFn(isBoolean(newValue) ? newValue : value)
      if (!result.success) {
        validationErrors.push(result)
      }
    })
    setErrors(validationErrors)
    return validationErrors.length === 0
  }

  useImperativeHandle(actionRef, () => ({
    validate: () => runValidations(value),
  }))

  return (
    <span className={clsx(className, styles.checkbox)}>
      <span className={styles.label} onClick={() => runValidations(!value)}>
        <input
          aria-label={typeof label === 'string' ? label : undefined}
          aria-labelledby={`${id}-label`}
          checked={value}
          disabled={disabled}
          id={id}
          onChange={() => runValidations(!value)}
          onKeyPress={(e) => {
            if (e.which === 13) {
              runValidations(!value)
            }
          }}
          type="checkbox"
        />
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label
          className={clsx(styles.checkbox, !!value && styles.checkboxActive)}
          htmlFor={id}
          id={`${id}-label`}
        />
        <label htmlFor={id}>
          <p className={styles.checkboxLabel}>{children || label}</p>
        </label>
      </span>
      {errors.length > 0 && (
        <p className={styles.error}>
          {t('common:ERROR_LABEL')}
          {': '}
          {errorIcon}
          {first(errors)!.error}
        </p>
      )}
    </span>
  )
}
